/**
 * Optional list-row fields for the admin Assessment Request grid.
 * Maps common API shapes; returns "" when absent so UI can show "--".
 */

import { getListRowLocationDisplay } from "./addressUtils";
import { getGridArchiveCommentDisplay } from "./gridArchiveCommentDisplay";
import { getRequestContactDisplayFromApiItem } from "./requestContactFormUtils";

export function getAssessmentRequestTypeDisplay(item) {
  const t =
    item?.assessment_type ??
    item?.vqa_assessment_type ??
    item?.assessmentType ??
    item?.document_type;
  if (t == null || t === "") return "";
  return String(t).trim();
}

export function getAssessmentRequestGxpDisplay(item) {
  const v = item?.gxp ?? item?.gxp_label ?? item?.gxp_name ?? item?.GxP;
  if (v == null || v === "") return "";
  return String(v).trim();
}

export function getAssessmentRequestLocationDisplay(item) {
  return getListRowLocationDisplay(item, { short: false, abbvState: false });
}

/**
 * Onsite vs Remote; canonical source is `vqa_type` ("onsite" | "remote").
 * Legacy bool fields (`onsite` / `remote`) are only used when `vqa_type` is absent.
 * Returns "" when absent so UI can show "--".
 */
export function getAssessmentRequestOnsiteRemoteDisplay(item) {
  const rawType = item?.vqa_type ?? item?.onsite_remote;
  if (rawType != null && rawType !== "") {
    const s = String(rawType).trim().toLowerCase();
    if (s === "onsite" || s === "on-site") return "Onsite";
    if (s === "remote") return "Remote";
    if (s === "true") return "Onsite";
    if (s === "false") return "Remote";
    return String(rawType).trim();
  }

  const delivery =
    item?.delivery_mode ?? item?.assessment_delivery_type;
  if (delivery != null && delivery !== "") {
    const s = String(delivery).trim().toLowerCase();
    if (s === "onsite" || s === "on-site") return "Onsite";
    if (s === "remote") return "Remote";
  }

  if (item?.onsite === true) return "Onsite";
  if (item?.remote === true) return "Remote";

  return "";
}

/**
 * Multi-Sponsor comes from the request flag `is_multi_sponsor_assessment`
 * (NOT `join_sponsor_request_count`, which counts other sponsors' join
 * requests). NULL/absent means legacy rows created before the flag existed;
 * they render as "Yes" because new requests always default the flag to true.
 */
export function isAssessmentRequestMultiSponsor(item) {
  return item?.is_multi_sponsor_assessment !== false;
}

export function getAssessmentRequestMultiSponsorDisplay(item) {
  return isAssessmentRequestMultiSponsor(item) ? "Yes" : "No";
}

/**
 * Auditor label for assessment list rows when the API includes it.
 * Prefer `vqa_auditor_name` (used across VQA list/detail payloads).
 */
export function getAssessmentRequestAuditorDisplay(item) {
  const name = item?.vqa_auditor_name ?? item?.auditor_name ?? "";
  return String(name).trim();
}

/**
 * Short date display aligned with Innovation Lab AssessmentGrid (`formatShortDate`): M-D-YY.
 * Parses YYYY-MM-DD as local components when possible to avoid timezone shift.
 */
export function formatAssessmentHistoryShortDate(value) {
  if (value == null || value === "") return "";
  try {
    const raw = typeof value === "string" ? value : String(value);
    if (raw === "--") return "--";
    const datePart = raw.split("T")[0];
    const parts = datePart.split("-");
    if (parts.length === 3) {
      const [year, month, day] = parts.map(Number);
      if (year && month && day) {
        return `${month}-${day}-${String(year).slice(-2)}`;
      }
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    const month = date.getMonth() + 1;
    const day = date.getDate();
    const year = date.getFullYear().toString().slice(-2);
    return `${month}-${day}-${year}`;
  } catch {
    return "";
  }
}

/** Optional list-row request contact display (Enhancement 10). */
export function getAssessmentRequestContactDisplay(item) {
  return getRequestContactDisplayFromApiItem(item);
}

/** Optional admin-only archive note on assessment request list rows. */
export function getAssessmentRequestArchiveCommentDisplay(item) {
  return getGridArchiveCommentDisplay(item);
}
